import { createTheme } from "@mui/material/styles";
import { colors, layout, radii, shadows, typography } from "./design/tokens";

// ── Life Circle design tokens ──────────────────────────────────────────────
const BG           = colors.canvas;
const SURFACE      = colors.surface;
const SURFACE_ALT  = colors.surfaceSubtle;
const BORDER       = colors.border;

const TEXT_PRIMARY   = colors.text;
const TEXT_SECONDARY = colors.textSecondary;
const TEXT_MUTED     = colors.textMuted;

// Accent (forest green)
const ACCENT       = colors.primary;
const ACCENT_LIGHT = colors.primarySubtle;
const ACCENT_MID   = colors.primaryMid;
const ACCENT_DARK  = colors.primaryDark;

// Warm (amber)
const WARM         = colors.warm;
const WARM_LIGHT   = colors.warmSubtle;

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 750,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    mode: "light",
    primary:    { main: ACCENT, dark: ACCENT_DARK, light: ACCENT_MID, contrastText: "#fff" },
    secondary:  { main: WARM, light: WARM_LIGHT, contrastText: "#fff" },
    warning:    { main: WARM },
    background: { default: BG, paper: SURFACE },
    text: {
      primary:   TEXT_PRIMARY,
      secondary: TEXT_SECONDARY,
      disabled:  TEXT_MUTED,
    },
    divider: BORDER,
  },
  typography: {
    fontFamily: typography.sans,
    body1: { fontSize: "1rem", lineHeight: 1.55 },
    body2: { fontSize: "0.875rem", lineHeight: 1.5 },
    caption: { fontSize: "0.75rem", lineHeight: 1.45 },
    h1: { fontFamily: typography.serif, fontWeight: 400 },
    h2: { fontFamily: typography.serif, fontWeight: 400 },
    h3: { fontFamily: typography.serif, fontWeight: 400 },
    h4: { fontFamily: typography.serif, fontWeight: 400 },
    h5: { fontFamily: typography.serif, fontWeight: 400 },
    h6: { fontFamily: typography.serif, fontWeight: 400 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: BG,
          color: TEXT_PRIMARY,
        },
        "*:focus-visible": {
          outline: `3px solid ${colors.focus}`,
          outlineOffset: 2,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radii.card,
          backgroundColor: SURFACE,
          border: `1px solid ${BORDER}`,
          boxShadow: shadows.card,
          transition: "transform 0.15s, box-shadow 0.15s",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 16px rgba(26,23,20,0.10)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: layout.touchTarget,
          borderRadius: radii.control,
        },
        containedPrimary: {
          borderRadius: radii.control,
          padding: "7px 20px",
          backgroundColor: ACCENT,
          "&:hover": { backgroundColor: ACCENT_DARK },
        },
        outlinedPrimary: {
          borderRadius: radii.control,
          borderColor: BORDER,
          color: TEXT_SECONDARY,
          "&:hover": { backgroundColor: SURFACE_ALT },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: SURFACE,
          color: TEXT_PRIMARY,
          borderBottom: `1px solid ${BORDER}`,
          boxShadow: "none",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: 56 },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: "'DM Sans', sans-serif",
          backgroundColor: SURFACE_ALT,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: BORDER },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: SURFACE, borderRadius: radii.sheet },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          width: layout.touchTarget,
          height: layout.touchTarget,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: layout.touchTarget,
          textTransform: "none",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: BORDER },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 20 },
        colorPrimary: {
          backgroundColor: ACCENT_LIGHT,
          color: ACCENT_DARK,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorError: { backgroundColor: WARM },
      },
    },
  },
});

export default theme;
